/**
 * @file usePickingSearch.ts
 * @description Поиск по позициям задания на сборку (phase19.23).
 *
 * Состав задания фиксирован заказом — добавить строку, как в приёмке, нельзя.
 * Зато нужно быстро найти позицию без сканера: по артикулу, названию, фасовке
 * или ячейке. Хук держит запрос, отфильтрованный список и решение, что делать
 * с завершённым вводом (`lib/picking-search.ts`) — страница остаётся про
 * раскладку.
 *
 * Правила один в один с мобильным `picking_detail_screen.dart`: код ячейки в
 * фильтр не попадает (это маршрут скана), скан и точный артикул отмечают
 * позицию и сбрасывают фильтр, остальной текст только ищет.
 */

import { useMemo, useState } from "react";

import {
  filterPickingItems,
  parsePickingInput,
  routePickingInput,
} from "../../../lib/picking-search";
import type { PickingTaskItemDto } from "../../../lib/api-picking";

export interface UsePickingSearchResult {
  /** Текущий запрос (пустой — фильтра нет). */
  query: string;
  /** Строки после фильтра — их и рисует список. */
  visibleItems: PickingTaskItemDto[];
  /** Живой ввод из поля. */
  changeQuery: (value: string) => void;
  /** Сбросить фильтр (кнопка «Сбросить», отметка позиции). */
  reset: () => void;
  /** Ввод завершён (Enter/скан): вернёт код для сервера либо null. */
  routeSubmit: (code: string, fromScanner: boolean) => string | null;
}

/** Стабильная пустая ссылка: задание ещё грузится, а хук уже живёт. */
const NO_ITEMS: PickingTaskItemDto[] = [];

export function usePickingSearch(
  /** Строки задания; `undefined`, пока оно не загружено. */
  taskItems: PickingTaskItemDto[] | undefined,
  taskActive: boolean,
): UsePickingSearchResult {
  const items = taskItems ?? NO_ITEMS;
  const [query, setQuery] = useState("");
  const visibleItems = useMemo(
    () => filterPickingItems(items, query),
    [items, query],
  );

  function changeQuery(value: string) {
    // Код ячейки — маршрут скана, а не поисковый запрос: в фильтр не пускаем.
    setQuery(parsePickingInput(value).kind === "bin" ? "" : value);
  }

  function routeSubmit(code: string, fromScanner: boolean): string | null {
    const routing = routePickingInput({
      value: code,
      fromScanner,
      items,
      taskActive,
    });
    // Фильтр уже применён живым вводом — странице делать нечего.
    if (routing === "ignore" || routing === "filter") return null;
    setQuery("");
    return code.trim();
  }

  return {
    query,
    visibleItems,
    changeQuery,
    reset: () => setQuery(""),
    routeSubmit,
  };
}
